import React from "react";
import Icon from "@/components/ui/Icon";

/** Page wrapper — consistent width & rhythm for admin listings */
export function ListingPageRoot({ children, className = "" }) {
  return (
    <div
      className={`font-jakarta mx-auto w-full min-w-0 max-w-[1800px] space-y-5 pb-8 ${className}`}
    >
      {children}
    </div>
  );
}

export function ListingPageHeader({ kicker, title, description, actions }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-stone-200/90 bg-white/95 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/90 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        {kicker ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-600 dark:text-teal-400">
            {kicker}
          </p>
        ) : null}
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
          {title}
        </h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

export function ListingStatGrid({ children, className = "" }) {
  return (
    <div
      className={`grid grid-cols-2 gap-3 lg:grid-cols-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function ListingStatCard({
  label,
  value,
  hint,
  icon,
  loading,
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-stone-200/90 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 ${
        loading ? "animate-pulse" : ""
      }`}
    >
      {!loading ? (
        <div
          className="absolute left-0 top-0 h-full w-1 rounded-r bg-teal-500/85"
          aria-hidden
        />
      ) : null}
      <div className="flex items-start justify-between gap-3 pl-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
            {loading ? "—" : value}
          </p>
          {hint && !loading ? (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
          ) : null}
        </div>
        {icon ? (
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-lg text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            <Icon icon={icon} />
          </span>
        ) : null}
      </div>
    </div>
  );
}

export const listingBtnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900";

export const listingBtnSecondary =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-stone-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800";

export const listingBtnMuted =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-stone-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700";

export const listingPanelClass =
  "rounded-2xl border border-stone-200/90 bg-white/95 shadow-sm dark:border-slate-800 dark:bg-slate-900/90";

/** Ant Design ConfigProvider theme for Tabs (teal ink bar — matches scraper / articles scraper) */
export const listingAntTabsTheme = {
  token: {
    colorPrimary: "#0d9488",
    borderRadius: 10,
  },
  components: {
    Tabs: {
      inkBarColor: "#0d9488",
      titleFontSize: 14,
      horizontalMargin: "0 4px 0 0",
      horizontalItemPadding: "10px 14px",
    },
  },
};

export const listingTableClass =
  "min-w-full divide-y divide-stone-100 text-left dark:divide-slate-800";

export const listingTheadClass =
  "bg-stone-50/90 dark:bg-slate-800/40";

export const listingThClass =
  "whitespace-nowrap px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400";

export const listingTdClass =
  "whitespace-nowrap px-4 py-3 text-sm text-slate-700 dark:text-slate-300";

export const listingTrClass =
  "transition hover:bg-teal-50/50 dark:hover:bg-slate-800/40";
